import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/base/Breadcrumb";
import { testimonialList } from "@/mocks/testimonials";
import type { Testimonial } from "@/types/content";

const PRINCIPLES = [
  {
    icon: "ri-shield-check-line",
    title: "多方印证再上榜",
    desc: "单一来源、缺少交叉印证的内容不会进入头部名次，详情页会明确标注「待核实」，避免以热度代替可信度。",
  },
  {
    icon: "ri-file-list-3-line",
    title: "保留事件完整脉络",
    desc: "不只用一句标题概括事件。我们用时间线、关键信息与多方说法还原经过，让读者自己判断。",
  },
  {
    icon: "ri-refresh-line",
    title: "出现反转就更新",
    desc: "后续出现澄清或反转时，我们在原文顶部补充更新说明，而不是悄悄删除或改写历史内容。",
  },
  {
    icon: "ri-user-unfollow-line",
    title: "不放大个人隐私",
    desc: "涉及普通人身份、住址、联系方式等内容一律不做呈现。爆料可以追，但不以伤害他人为代价。",
  },
];

const MILESTONES = [
  { year: "初衷", title: "从一份「今日热点清单」开始", desc: "最初只是编辑部内部用来掌握当天讨论走向的清单，后来被越来越多同行借用。" },
  { year: "成形", title: "确立榜单三层结构", desc: "把热度拆成爆发、持续与长期三个维度，于是有了今日榜、本周榜与本月榜。" },
  { year: "现在", title: "六个常驻栏目稳定更新", desc: "覆盖娱乐、影视、科技、游戏、社会与商业，每个栏目由固定编辑维护。" },
];

export default function About() {
  const voices: Testimonial[] = testimonialList;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "关于我们 - 瓜点 · 网络爆料雷达",
      url: "https://sunshinelabs.cn/about",
      description: "了解瓜点 · 网络爆料雷达的网站定位、发展理念、内容原则与用户口碑。",
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "瓜点 · 网络爆料雷达",
      url: "https://sunshinelabs.cn/",
      email: "contact@guadian.example.com",
      description: "聚合当日网络爆料、最新头条与全网热议榜单的内容站点。",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://sunshinelabs.cn/" },
        { "@type": "ListItem", position: 2, name: "关于我们", item: "https://sunshinelabs.cn/about" },
      ],
    },
  ];

  return (
    <SiteLayout>
      <Seo
        title="关于我们 - 网站定位与内容原则 | 瓜点爆料雷达"
        description="瓜点 · 网络爆料雷达是一个聚合当日网络爆料与热议榜单的内容站点。了解我们的网站定位、发展理念、内容原则与读者口碑。"
        keywords="网络爆料,关于我们,科普,内容原则,爆料榜单"
        path="/about"
        ogType="website"
        jsonLd={jsonLd}
      />

      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1240px] px-4 py-8 md:px-6 md:py-14">
          <Breadcrumb items={[{ label: "关于我们" }]} />
          <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-7">
              <span className="font-label text-xs tracking-[0.28em] text-primary-600">
                ABOUT GUADIAN
              </span>
              <h1 className="mt-3 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-4xl">
                把每天的爆料，整理成一份读得完的热度地图
              </h1>
              <p className="mt-5 text-sm leading-relaxed text-foreground-700 md:text-base">
                瓜点 · 网络爆料雷达是一个专注热点聚合的内容站点。我们没有长篇大论的评论，更想做的是把散落在各个平台的讨论收拢起来，
                按热度排好顺序，标清时间与来源，让你花十分钟就能掌握当天的舆论走向。
              </p>
              <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                我们相信，吃瓜这件事本身没有问题，问题在于信息太碎、真假混在一起。所以站内每一条爆料都会给出关键信息与多方说法，
                把判断权交回给读者。
              </p>
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Link
                  to="/rank"
                  className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  查看今日热议榜单
                </Link>
                <Link
                  to="/contact"
                  className="whitespace-nowrap rounded-md border border-background-300 px-5 py-3 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  提供爆料线索
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
                <div className="h-[200px] w-full overflow-hidden md:h-[240px]">
                  <img
                    src="https://readdy.ai/api/search-image?query=Abstract%20editorial%20illustration%20of%20a%20tidy%20newsroom%20desk%20with%20paper%20clippings%2C%20a%20magnifier%20and%20stacked%20notes%20on%20warm%20cream%20background%2C%20deep%20moss%20green%20and%20amber%20accents%2C%20minimal%20flat%20design%2C%20soft%20grain%20texture%2C%20no%20text&width=900&height=700&seq=about-editorial-desk-01&orientation=landscape"
                    alt="瓜点编辑部内容整理工作场景示意"
                    title="瓜点 · 网络爆料雷达 内容整理"
                    className="h-full w-full object-cover"
                  />
                </div>
                <dl className="grid grid-cols-2 divide-x divide-background-200 border-t border-background-200">
                  {[
                    { k: "常驻栏目", v: "6 个" },
                    { k: "榜单刷新", v: "每小时" },
                    { k: "爆料流更新", v: "全天滚动" },
                    { k: "编辑复核", v: "每条" },
                  ].map((item) => (
                    <div key={item.k} className="p-4 md:p-5">
                      <dt className="text-xs text-foreground-500">{item.k}</dt>
                      <dd className="mt-1 font-heading text-xl font-bold text-foreground-950">
                        {item.v}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full border-b border-background-200 bg-background-50">
        <div className="mx-auto w-full max-w-[1240px] px-4 py-12 md:px-6 md:py-16">
          <div className="max-w-2xl">
            <span className="font-label text-xs tracking-[0.28em] text-secondary-600">
              POSITIONING
            </span>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              网站定位
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              服务三类人：想快速了解当天热点的普通读者、需要选题参考的内容创作者，以及关注舆论走向的品牌从业者。
            </p>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
            {[
              {
                icon: "ri-user-smile-line",
                title: "给吃瓜读者",
                desc: "不用在多个平台之间来回切换，一页看完今日最值得追的话题与后续进展。",
              },
              {
                icon: "ri-edit-2-line",
                title: "给内容创作者",
                desc: "关键信息已整理成条目，直接作为选题参考与背景核对，省掉大量资料翻找时间。",
              },
              {
                icon: "ri-line-chart-line",
                title: "给品牌从业者",
                desc: "通过今日榜与本周榜的对比，判断一件事是在持续发酵还是正在降温。",
              },
            ].map((item) => (
              <div
                key={item.title}
                className="rounded-lg border border-background-200 bg-background-100 p-5"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                  <i className={`${item.icon} text-xl`}></i>
                </span>
                <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1240px] px-4 py-12 md:px-6 md:py-16">
          <div className="max-w-2xl">
            <span className="font-label text-xs tracking-[0.28em] text-accent-700">PRINCIPLES</span>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              内容原则
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              这四条是我们整理每一条爆料时都会执行的底线，也是判断一条内容该不该上榜的依据。
            </p>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {PRINCIPLES.map((p, idx) => (
              <div
                key={p.title}
                className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                  <i className={`${p.icon} text-xl`}></i>
                </span>
                <div>
                  <h3 className="font-heading text-base font-bold text-foreground-950">
                    <span className="mr-2 font-label text-xs text-foreground-400">
                      0{idx + 1}
                    </span>
                    {p.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full border-b border-background-200 bg-background-50">
        <div className="mx-auto w-full max-w-[1240px] px-4 py-12 md:px-6 md:py-16">
          <div className="max-w-2xl">
            <span className="font-label text-xs tracking-[0.28em] text-primary-600">GROWTH</span>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              发展理念
            </h2>
          </div>
          <ol className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
            {MILESTONES.map((m, idx) => (
              <li key={m.year} className="relative rounded-lg border border-background-200 bg-background-100 p-5">
                <span className="flex items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent-500 font-heading text-sm font-bold text-accent-950">
                    {idx + 1}
                  </span>
                  <span className="font-label text-xs tracking-[0.2em] text-foreground-500">
                    {m.year}
                  </span>
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
                  {m.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{m.desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1240px] px-4 py-12 md:px-6 md:py-16">
          <div className="max-w-2xl">
            <span className="font-label text-xs tracking-[0.28em] text-secondary-600">VOICES</span>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              用户口碑
            </h2>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {voices.map((v) => (
              <figure
                key={v.name}
                className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <span className="flex h-8 w-8 items-center justify-center text-accent-600">
                  <i className="ri-double-quotes-l text-2xl"></i>
                </span>
                <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">
                  {v.content}
                </blockquote>
                <figcaption className="mt-4 flex items-center gap-3 border-t border-background-200 pt-4">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-500 font-heading text-sm font-bold text-background-50">
                    {v.initial}
                  </span>
                  <span className="flex flex-col">
                    <span className="text-sm font-medium text-foreground-950">{v.name}</span>
                    <span className="text-xs text-foreground-500">{v.role}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50">
        <div className="mx-auto flex w-full max-w-[1240px] flex-col items-start justify-between gap-6 px-4 py-12 md:flex-row md:items-center md:px-6 md:py-16">
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground-950">
              有线索，或者只是想聊聊？
            </h2>
            <p className="mt-2 text-sm text-foreground-600">
              欢迎通过邮箱 contact@guadian.example.com 联系我们，或直接在联系页面留言。
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to="/contact"
              className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              前往联系页面
            </Link>
            <Link
              to="/"
              className="whitespace-nowrap rounded-md border border-background-300 px-5 py-3 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              回到首页
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}